import React from 'react'
import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import styled from 'styled-components'

/*
 * Data Analytics 화면들이 함께 쓰는 조각.
 *
 * 세 화면(FANZA 메시지 포인트, 스티팝 이미지·영상, 스티팝 채팅 코인) 모두
 * 「기간을 골라 서버 집계를 받아 표로 그린다」는 구조가 같아, 기간 상태 관리와
 * 표 스타일을 여기 모았다. 집계는 전부 서버가 하고 화면은 표시만 한다.
 */

/// 오늘로부터 n일 전 날짜를 YYYY-MM-DD 로. 프리셋 버튼과 기본값이 함께 쓴다.
/// toISOString 은 UTC 라 KST 오전 9시 이전에 하루 밀린다 — 로컬 날짜로 직접 조립한다.
export const dayAgo = n => {
  const date = new Date()
  date.setDate(date.getDate() - n)
  const pad = v => String(v).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export const PRESETS = [
  { id: 'today', label: '오늘', days: 1 },
  { id: 'week', label: '1주일', days: 7 },
  { id: 'month', label: '1달', days: 30 },
]

export const num = value =>
  value === null || value === undefined ? '-' : value.toLocaleString()

export const percent = value => (value === null || value === undefined ? '-' : `${value}%`)

/// 모델이나 기능을 되짚을 수 없어 괄호로 표시한 행. 표에서 실제 값과 섞이지 않게 흐리게 둔다.
export const isPlaceholder = label => typeof label === 'string' && label.startsWith('(')

export const GrowthValue = ({ value }) => {
  if (value === null || value === undefined) return <Muted>신규</Muted>
  if (value === 0) return <Muted>0%</Muted>
  return <Growth up={value > 0}>{value > 0 ? `+${value}%` : `${value}%`}</Growth>
}

/**
 * 기간을 고르고 그 기간의 집계를 받아 오는 상태 묶음.
 *
 * 입력칸은 초안이고 실제 조회는 applied 로만 돈다. 조회 1회가 큰 테이블을 훑기 때문에
 * 날짜를 만지는 도중의 어중간한 구간(시작일만 옮긴 상태 등)으로는 요청을 보내지 않는다.
 *
 * 응답 봉투가 서버마다 다르다. fanza-api 는 { header, body } 를 쓰고 adminapi 는
 * { status, ...본문 } 을 쓰기 때문에, 본문을 꺼내는 방법을 unwrap 으로 받는다.
 *
 * @param {string}   url      기간 파라미터를 뺀 엔드포인트 주소
 * @param {object|function} headers  요청 헤더. 매 요청마다 새로 읽어야 하면 함수로 준다.
 * @param {function} unwrap   응답에서 본문을 꺼내는 함수 (기본: data.body)
 */
export const usePeriodData = ({ url, headers, unwrap = data => data.body }) => {
  const [startDate, setStartDate] = useState(dayAgo(6))
  const [endDate, setEndDate] = useState(dayAgo(0))
  const [applied, setApplied] = useState({ startDate: dayAgo(6), endDate: dayAgo(0) })
  const [body, setBody] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const requestSeq = useRef(0)

  const invalidRange = !startDate || !endDate || startDate > endDate
  const isDirty = startDate !== applied.startDate || endDate !== applied.endDate

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError('')

      // 넓은 구간은 십수 초까지 걸린다. 늦게 온 이전 요청이 최신 결과를 덮어쓰지 않게 순번을 확인한다.
      const seq = ++requestSeq.current
      const isStale = () => cancelled || seq !== requestSeq.current

      await axios
        .get(`${url}?startDate=${applied.startDate}&endDate=${applied.endDate}`, {
          headers: typeof headers === 'function' ? headers() : headers,
        })
        .then(({ data }) => {
          if (isStale()) return
          setBody(unwrap(data) || {})
        })
        .catch(err => {
          if (isStale()) return
          console.error(`[${url}] error:`, err)
          const data = (err.response && err.response.data) || {}
          const msg = (data.header && data.header.message) || data.message
          setError(msg ? `데이터를 불러오지 못했습니다: ${msg}` : '데이터를 불러오지 못했습니다.')
        })
        .finally(() => {
          if (!isStale()) setLoading(false)
        })
    }

    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied, url])

  const search = () => {
    if (invalidRange || !isDirty) return
    setApplied({ startDate, endDate })
  }

  /// 프리셋은 한 번 누르는 것이 곧 의도라 초안과 조회를 함께 갱신한다.
  const applyPreset = days => {
    const next = { startDate: dayAgo(days - 1), endDate: dayAgo(0) }
    setStartDate(next.startDate)
    setEndDate(next.endDate)
    setApplied(next)
  }

  const activePreset = PRESETS.find(
    p => applied.startDate === dayAgo(p.days - 1) && applied.endDate === dayAgo(0) && !isDirty
  )

  const range = body.range || {}

  return {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    body,
    loading,
    error,
    invalidRange,
    isDirty,
    search,
    applyPreset,
    activePreset,
    rangeLabel: range.label || '',
    prevLabel: range.prevStartDate ? `${range.prevStartDate} ~ ${range.prevEndDate}` : '',
  }
}

/// 프리셋 · 날짜 입력 · 조회 버튼 · 요약 한 줄. summary 는 조회가 끝난 뒤에만 보인다.
export const PeriodControls = ({ state, summary }) => {
  const {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    loading,
    invalidRange,
    isDirty,
    search,
    applyPreset,
    activePreset,
    rangeLabel,
  } = state

  return (
    <Controls>
      <Presets>
        {PRESETS.map(preset => (
          <PresetButton
            key={preset.id}
            active={activePreset && activePreset.id === preset.id}
            onClick={() => applyPreset(preset.days)}
          >
            {preset.label}
          </PresetButton>
        ))}
      </Presets>

      <DateRange onKeyDown={e => e.key === 'Enter' && search()}>
        <input
          type="date"
          value={startDate}
          max={endDate}
          onChange={e => setStartDate(e.target.value)}
        />
        <span>~</span>
        <input
          type="date"
          value={endDate}
          min={startDate}
          max={dayAgo(0)}
          onChange={e => setEndDate(e.target.value)}
        />
        <SearchButton
          ready={isDirty && !invalidRange}
          disabled={!isDirty || invalidRange || loading}
          onClick={search}
        >
          조회
        </SearchButton>
      </DateRange>

      <Summary>
        {rangeLabel && <strong>{rangeLabel}</strong>}
        {invalidRange ? (
          <Pending>시작일이 종료일보다 늦습니다.</Pending>
        ) : (
          isDirty && <Pending>조회를 눌러야 반영됩니다.</Pending>
        )}
        {!loading && !isDirty && summary}
      </Summary>
    </Controls>
  )
}

export const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 0 15px 30px;
  box-sizing: border-box;
`

const Controls = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 10px;
  row-gap: 8px;
  margin: 15px 0;
`

const Presets = styled.div`
  display: flex;
`

const PresetButton = styled.button`
  height: 30px;
  padding: 0 12px;
  border: 1px solid ${props => (props.active ? '#3c8dbc' : '#d2d6de')};
  background-color: ${props => (props.active ? '#3c8dbc' : '#fff')};
  color: ${props => (props.active ? '#fff' : '#555')};
  font-size: 12px;
  cursor: pointer;

  & + & {
    margin-left: -1px;
  }

  &:first-child {
    border-radius: 3px 0 0 3px;
  }

  &:last-child {
    border-radius: 0 3px 3px 0;
  }
`

const DateRange = styled.div`
  display: flex;
  align-items: center;
  column-gap: 6px;
  font-size: 12px;
  color: #888;

  input {
    height: 30px;
    padding: 0 8px;
    border: 1px solid #d2d6de;
    border-radius: 3px;
    font-size: 12px;
    color: #555;
  }
`

const SearchButton = styled.button`
  height: 30px;
  margin-left: 4px;
  padding: 0 14px;
  border: 1px solid ${props => (props.ready ? '#00a65a' : '#d2d6de')};
  border-radius: 3px;
  background-color: ${props => (props.ready ? '#00a65a' : '#f4f4f4')};
  color: ${props => (props.ready ? '#fff' : '#aaa')};
  font-size: 12px;
  font-weight: 600;
  cursor: ${props => (props.ready ? 'pointer' : 'default')};
`

const Pending = styled.span`
  color: #f39c12;
`

const Summary = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
  margin-left: 10px;
  font-size: 13px;
  color: #444;
`

export const Message = styled.div`
  padding: 30px 0;
  color: #888;
  font-size: 13px;
`

export const TableBox = styled.div`
  width: 100%;
  overflow-x: auto;
  background-color: #fff;
  border: 1px solid #d2d6de;
`

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  tbody tr:nth-child(even) {
    background-color: #fafafa;
  }
`

export const Th = styled.th`
  padding: 8px 10px;
  border-bottom: 2px solid #d2d6de;
  text-align: ${props => props.align || 'right'};
  white-space: nowrap;
  color: #555;
  font-weight: 600;
`

export const Td = styled.td`
  padding: 8px 10px;
  border-bottom: 1px solid #eee;
  text-align: ${props => props.align || 'right'};
  white-space: ${props => (props.align === 'left' ? 'normal' : 'nowrap')};
  vertical-align: top;
`

export const TotalFoot = styled.tfoot`
  background-color: #f4f4f4;

  td {
    border-top: 2px solid #d2d6de;
    font-weight: 600;
  }
`

export const Name = styled.div`
  font-weight: 600;
  color: ${props => (props.faded ? '#aaa' : 'inherit')};
`

export const Note = styled.div`
  margin-top: 3px;
  color: #999;
  font-size: 11px;
  font-weight: 400;
`

export const Muted = styled.span`
  color: #aaa;
`

const Growth = styled.span`
  color: ${props => (props.up ? '#00a65a' : '#dd4b39')};
  font-weight: 600;
`

export const Footnote = styled.p`
  margin-top: 12px;
  color: #888;
  font-size: 11px;
  line-height: 1.6;
`
